import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategy } from '@snap-types/models/enums';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { OnUnitPriceChangeInput } from '../types';
import { applyQuotePriceRecalcResult } from '../utilities/apply-quote-price-recalc-result';
import { buildStrategyCalculations } from '../utilities/build-strategy-calculations';

export function onPartUnitPriceChange(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    unitPrice: Decimal;
    priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );
  const { priceStrategy } = quotePrice;

  const inputs = {
    state,
    quoteLine,
    quotePrice,
    unitPrice: params.unitPrice,
    priceStrategyConfigs: params.priceStrategyConfigs,
  } satisfies OnUnitPriceChangeInput;

  const calculation =
    buildStrategyCalculations('onUnitPriceChange')[priceStrategy];

  if (!calculation) {
    console.error(
      'No unit price calculation function found for price strategy',
      priceStrategy,
    );
    return;
  }

  const result = calculation(inputs);

  if (result.status === 'failure') {
    // @TODO(NT): Our store should be integrated with toast notifications
    console.error('Failed to calculate price', result.reason);
    return;
  }

  applyQuotePriceRecalcResult(state, {
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    quotePrice,
    patch: {
      unitPrice: params.unitPrice,
      userUnitPrice: params.unitPrice,
      calculatedUnitPrice: null,
      suggestedPriceStatus: SnapSuggestionStatus.REJECTED,
    },
  });

  if (result.status === 'success' || result.status === 'no-op') {
    const calculatedGrossMargin = result.grossMargin ?? null;
    const tracksCalculatedGrossMargin =
      priceStrategy !== PriceStrategy.CAPPriceSegmentation &&
      priceStrategy !== PriceStrategy.PriceSegmentation &&
      priceStrategy !== PriceStrategy.PriceSegmentationWithTargetGrossMargin;

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        userGrossMargin: null,
        grossMargin: result.grossMargin ?? null,
        calculatedGrossMargin: tracksCalculatedGrossMargin
          ? calculatedGrossMargin
          : null,
        contributionMargin: result.contributionMargin ?? null,
        userContributionMargin: null,
        calculatedContributionMargin: result.contributionMargin ?? null,
      },
      rawFieldsToClear: ['grossMargin', 'contributionMargin'],
    });
  }

  quotePrice.totalPrice = calculateTotal(
    params.unitPrice,
    quotePrice.quantity,
    SnapRoundingOptionsCustom(state),
  );

  quoteLine.automatable = false;
  if (quotePrice.suggestedPriceStatus === SnapSuggestionStatus.REJECTED) {
    quoteLine.suggestedPricesStatus = SnapSuggestionStatus.REJECTED;
  }
}
